import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, Stack, Switch, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import unifiedApi from '../../utils/unifiedApi';
import {
  disablePushOnDevice, enablePushOnDevice, getCurrentSubscription, getNotificationPermission,
  isPushSupported, needsInstallForPush,
} from '../../utils/pushNotifications';

const DEFAULT_QUIET_START = '22:00';
const DEFAULT_QUIET_END = '07:00';

export const IosInstallHelp = () => (
  <Alert severity="info">
    To get notifications on iPhone or iPad, add Quail Game to your Home Screen: tap the
    <strong> Share </strong> button, choose <strong>Add to Home Screen</strong>, then open the app from your Home
    Screen and turn notifications on here.
  </Alert>
);

/**
 * Chat notification settings: this device (push subscription), all chat notifications for the
 * account (every device), and quiet hours. Per-chat mute lives in each conversation header.
 */
const ChatNotificationSettingsDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [deviceEnabled, setDeviceEnabled] = useState(false);
  const [permission, setPermission] = useState(getNotificationPermission());
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaved(false);
    (async () => {
      try {
        const [configData, prefs, subscription] = await Promise.all([
          unifiedApi.getChatPushConfig(),
          unifiedApi.getChatNotificationPreferences(),
          getCurrentSubscription().catch(() => null),
        ]);
        if (cancelled) return;
        setConfig(configData);
        setPreferences({
          notificationsEnabled: prefs?.notificationsEnabled ?? true,
          quietHoursEnabled: prefs?.quietHoursEnabled ?? false,
          quietHoursStart: prefs?.quietHoursStart || DEFAULT_QUIET_START,
          quietHoursEnd: prefs?.quietHoursEnd || DEFAULT_QUIET_END,
        });
        setDeviceEnabled(Boolean(subscription) && getNotificationPermission() === 'granted');
        setPermission(getNotificationPermission());
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load notification settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggleDevice = async (enable) => {
    setDeviceBusy(true);
    setError(null);
    try {
      if (enable) {
        // Must run directly from the tap so browsers allow the permission prompt.
        const result = await enablePushOnDevice(config.vapidPublicKey);
        setPermission(result);
        setDeviceEnabled(result === 'granted');
        if (result === 'denied') setError('Notifications are blocked. Allow them for this site in your browser or phone settings.');
      } else {
        await disablePushOnDevice();
        setDeviceEnabled(false);
      }
    } catch (err) {
      setError(err.message || 'Could not update notifications on this device.');
    } finally {
      setDeviceBusy(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await unifiedApi.updateChatNotificationPreferences(preferences);
      if (result) {
        setPreferences({
          ...result,
          quietHoursStart: result.quietHoursStart || preferences.quietHoursStart,
          quietHoursEnd: result.quietHoursEnd || preferences.quietHoursEnd,
        });
      }
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Failed to save notification settings.');
    } finally {
      setSaving(false);
    }
  };

  const renderDeviceSection = () => {
    if (!config?.enabled) {
      return <Typography color="text.secondary">Push notifications aren&apos;t available yet.</Typography>;
    }
    if (needsInstallForPush()) return <IosInstallHelp />;
    if (!isPushSupported()) {
      return <Typography color="text.secondary">This browser doesn&apos;t support notifications.</Typography>;
    }
    return (
      <>
        <FormControlLabel
          control={(
            <Switch
              checked={deviceEnabled}
              onChange={(event) => toggleDevice(event.target.checked)}
              disabled={deviceBusy || (permission === 'denied' && !deviceEnabled)}
            />
          )}
          label="Notifications on this device"
        />
        {permission === 'denied' && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Notifications are blocked for this site. Allow them in your browser or phone settings, then try again.
          </Alert>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="sm">
      <DialogTitle>Chat notifications</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            <Box>
              <Typography variant="subtitle2" gutterBottom>This device</Typography>
              {renderDeviceSection()}
            </Box>

            <Divider />

            {preferences && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>All your devices</Typography>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={preferences.notificationsEnabled}
                      onChange={(event) => setPreferences((p) => ({ ...p, notificationsEnabled: event.target.checked }))}
                    />
                  )}
                  label="Chat notifications"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={preferences.quietHoursEnabled}
                      onChange={(event) => setPreferences((p) => ({ ...p, quietHoursEnabled: event.target.checked }))}
                      disabled={!preferences.notificationsEnabled}
                    />
                  )}
                  label="Quiet hours"
                />
                {preferences.quietHoursEnabled && preferences.notificationsEnabled && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      type="time"
                      label="From"
                      size="small"
                      value={preferences.quietHoursStart}
                      onChange={(event) => setPreferences((p) => ({ ...p, quietHoursStart: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      fullWidth={isMobile}
                    />
                    <TextField
                      type="time"
                      label="To"
                      size="small"
                      value={preferences.quietHoursEnd}
                      onChange={(event) => setPreferences((p) => ({ ...p, quietHoursEnd: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      fullWidth={isMobile}
                    />
                  </Stack>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Quiet hours use your organization&apos;s time zone. To mute one chat, tap the bell in that chat.
                </Typography>
                {saved && <Alert severity="success" sx={{ mt: 1 }}>Notification settings saved.</Alert>}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={savePreferences} disabled={loading || saving || !preferences}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatNotificationSettingsDialog;
